/**
 * Liman sırası önerisi (ADR 0048 §5): başlangıç ve bitiş sabit, ara limanlar en kısa toplam
 * mesafeye göre. Küçük n'de tam arama, büyükte en yakın komşu + 2-opt. Öneri yalnız gösterilir;
 * ticari kısıtlar (yükleme sırası, laycan) bilinmediğinden kullanıcı kabul eder.
 */

/** Tam aramanın üst sınırı (ara liman sayısı): 8! = 40 320 permütasyon. */
export const EXACT_ORDER_LIMIT = 8;

type Matrix = readonly (readonly (number | null)[])[];

const leg = (m: Matrix, a: number, b: number) => m[a]?.[b] ?? null;

/** Sıranın toplam mesafesi; bacaklardan biri bilinmiyorsa null. */
export function routeTotal(m: Matrix, sequence: readonly number[]): number | null {
  let total = 0;
  for (let i = 1; i < sequence.length; i++) {
    const d = leg(m, sequence[i - 1] ?? -1, sequence[i] ?? -1);
    if (d === null) return null;
    total += d;
  }
  return total;
}

export interface PortOrderInput {
  start: number;
  end: number;
  middle: readonly number[];
}

export interface PortOrderSuggestion {
  /** Ara limanların önerilen sırası. */
  order: number[];
  totalNm: number;
  /** Tam arama mı (en iyi sonuç kesin), sezgisel mi. */
  exact: boolean;
}

function exactOrder(m: Matrix, { start, end, middle }: PortOrderInput) {
  let best: { order: number[]; total: number } | null = null;
  const used = new Array<boolean>(middle.length).fill(false);
  const path: number[] = [];
  const visit = (last: number, sum: number) => {
    if (best && sum >= best.total) return;
    if (path.length === middle.length) {
      const close = leg(m, last, end);
      if (close === null) return;
      if (!best || sum + close < best.total) best = { order: [...path], total: sum + close };
      return;
    }
    for (let i = 0; i < middle.length; i++) {
      if (used[i]) continue;
      const next = middle[i] ?? -1;
      const d = leg(m, last, next);
      if (d === null) continue;
      used[i] = true;
      path.push(next);
      visit(next, sum + d);
      path.pop();
      used[i] = false;
    }
  };
  visit(start, 0);
  return best as { order: number[]; total: number } | null;
}

function heuristicOrder(m: Matrix, { start, end, middle }: PortOrderInput) {
  const remaining = new Set(middle);
  const order: number[] = [];
  let last = start;
  while (remaining.size > 0) {
    let pick: number | null = null;
    let pickD = Number.POSITIVE_INFINITY;
    for (const candidate of remaining) {
      const d = leg(m, last, candidate);
      if (d !== null && d < pickD) {
        pick = candidate;
        pickD = d;
      }
    }
    if (pick === null) return null;
    order.push(pick);
    remaining.delete(pick);
    last = pick;
  }
  const full = [start, ...order, end];
  let total = routeTotal(m, full);
  if (total === null) return null;
  // 2-opt: iç parçayı ters çevirmek kısaltıyorsa uygula (uçlar sabit)
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 1; i < full.length - 2; i++) {
      for (let k = i + 1; k < full.length - 1; k++) {
        const candidate = [
          ...full.slice(0, i),
          ...full.slice(i, k + 1).reverse(),
          ...full.slice(k + 1),
        ];
        const t = routeTotal(m, candidate);
        if (t !== null && t < total - 1e-9) {
          full.splice(0, full.length, ...candidate);
          total = t;
          improved = true;
        }
      }
    }
  }
  return { order: full.slice(1, -1), total };
}

/** Ara limanların en kısa sırası; uygun sıra bulunamazsa (bilinmeyen bacak) null. */
export function suggestPortOrder(m: Matrix, input: PortOrderInput): PortOrderSuggestion | null {
  const exact = input.middle.length <= EXACT_ORDER_LIMIT;
  const found = exact ? exactOrder(m, input) : heuristicOrder(m, input);
  if (!found) return null;
  // Sezgisel sonuç mevcut sıradan kötüyse mevcut sıra kalır
  const current = routeTotal(m, [input.start, ...input.middle, input.end]);
  if (current !== null && current < found.total) {
    return { order: [...input.middle], totalNm: current, exact };
  }
  return { order: found.order, totalNm: found.total, exact };
}
